import { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  useTheme,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import {
  AddBox as AddBoxIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  PostAdd as PostAddIcon,
} from "@mui/icons-material";
import DatePicker from "react-datepicker";

import axios from "axios";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";

import Layout from "../components/layout/Layout";
import SearchBar from "./SearchBar";
import Journalentry from "./Journalentry";
import AdjustmentEntry from "./AdjustmentEntry";
import { adjustmententry } from "../dashboardRow";

import "react-datepicker/dist/react-datepicker.css";
import "../styles/journal.css";

import configuration from "../config";

const Journal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalData, setJournalData] = useState({
    startDate: new Date(),
    journalNo: "Automatic",
    manualEntryData: "",
    invoiceDate: new Date(),
    invoiceNo: "",
    ledgerEntries: [
      { ledgerId: "", ledgerName: "", debit: "", credit: "", description: "" },
    ],
    selectedValue: "",
    selectedEntry: null,
  });

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [ledgers, setLedgers] = useState([]);
  const { API_BASE_URL } = configuration;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
 

  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const hideToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchLedgerData();
  }, [selectedCompanyId]);

  useEffect(() => {
    const fetchJournalEntries = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/journalentries/${selectedCompanyId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch journal entries");
        }
        const data = await response.json();
        setJournalEntries(data.data);
        showToast("Journal entries loaded successfully", "info");
      } catch (error) {
        console.error("Error fetching journal entries:", error);
        showToast("Failed to load journal entries", "error");
      }
    };

    fetchJournalEntries();
  }, []);

  const generateAutomaticJournalNo = () => {
    return `J-${Date.now()}`;
  };

  const fetchLedgerData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/ledgerdata/${selectedCompanyId}`
      );
      

      if (!response.data || response.data.length === 0) {
        
      } else {
        setLedgers(response.data);
        showToast("Ledger data loaded successfully", "success");
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);

     
    }
  };

  const handleInputChange = (field, value) => {
    setJournalData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleLedgerChange = (index, field, value) => {
    const newLedgerEntries = [...journalData.ledgerEntries];
    if (field === "ledgerId") {
      const selectedLedger = ledgers.find((ledger) => ledger._id === value);
      newLedgerEntries[index] = {
        ...newLedgerEntries[index],
        ledgerId: value,
        ledgerName: selectedLedger ? selectedLedger.LegerName : "",
      };
    } else {
      newLedgerEntries[index][field] = value;
    }
    handleInputChange("ledgerEntries", newLedgerEntries);
  };

  const handleAddLedger = () => {
    handleInputChange("ledgerEntries", [
      ...journalData.ledgerEntries,
      { ledgerId: "", ledgerName: "", debit: "", credit: "", description: "" },
    ]);
    showToast("New ledger entry added", "success");
  };

  const handleDeleteLedger = (index) => {
    if (journalData.ledgerEntries.length <= 1) {
      showToast("Cannot delete the last entry", "warning");
      return;
    }
    const newLedgerEntries = journalData.ledgerEntries.filter(
      (_, i) => i !== index
    );
    handleInputChange("ledgerEntries", newLedgerEntries);
    showToast("Ledger entry deleted", "info");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      journalData.ledgerEntries.some(
        (entry) => !entry.ledgerId || (!entry.debit && !entry.credit)
      )
    ) {
      showToast(
        "Please fill in all required fields for ledger entries",
        "error"
      );
      return;
    }

    let journalNumber;
    if (journalData.journalNo === "Automatic") {
      journalNumber = generateAutomaticJournalNo();
    } else if (
      journalData.journalNo === "Manual" &&
      journalData.manualEntryData
    ) {
      journalNumber = journalData.manualEntryData;
    } else {
      showToast(
        "Please provide a manual journal number or select Automatic",
        "error"
      );
      return;
    }

    const totalDebit = journalData.ledgerEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.debit) || 0),
      0
    );
    const totalCredit = journalData.ledgerEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.credit) || 0),
      0
    );

    // Check if entry is balanced
    if (totalDebit !== totalCredit) {
      showToast(
        "Journal entry is not balanced. Debit and Credit totals must match",
        "error"
      );
      return;
    }

    const submissionData = {
      kycId: selectedCompanyId,
      entryType: journalData.journalNo,
      journalNumber: journalNumber,
      entryDate: journalData.startDate.toISOString(),
      invoiceDate: journalData.invoiceDate.toISOString(),
      invoiceNo: journalData.invoiceNo,
      ledgerEntries: journalData.ledgerEntries.map((entry) => ({
        ledger: entry.ledgerName,
        debit: parseFloat(entry.debit) || 0,
        credit: parseFloat(entry.credit) || 0,
        description: entry.description,
      })),
      totalDebit: totalDebit,
      totalCredit: totalCredit,
      entryStatus: "Draft",
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/journal`,
        submissionData
      );
      
      showToast(
        `Journal entry submitted successfully! Journal Number: ${journalNumber}`,
        "success"
      );

      // Reset form
      setJournalData({
        startDate: new Date(),
        journalNo: "Automatic",
        manualEntryData: "",
        invoiceDate: new Date(),
        invoiceNo: "",
        ledgerEntries: [
          {
            ledgerId: "",
            ledgerName: "",
            debit: "",
            credit: "",
            description: "",
          },
        ],
        selectedValue: "",
        selectedEntry: null,
      });
    } catch (error) {
      console.error("Error submitting journal entry:", error);
      showToast("Error submitting journal entry. Please try again", "error");
    }
  };

  const renderResponsiveLedgerEntries = () => {
    if (isMobile) {
      return journalData.ledgerEntries.map((entry, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Card
            sx={{ mb: 2, borderRadius: "12px", border: "1px solid #E9ECEF" }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="600"
                  color="primary"
                >
                  Entry #{index + 1}
                </Typography>
                {journalData.ledgerEntries.length > 1 && (
                  <Tooltip title="Delete Entry">
                    <IconButton
                      onClick={() => handleDeleteLedger(index)}
                      size="small"
                      sx={{
                        color: theme.palette.error.main,
                        "&:hover": {
                          backgroundColor: "rgba(231, 76, 60, 0.08)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    mb={1}
                    display="block"
                  >
                    Ledger Name
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={entry.ledgerId}
                      onChange={(e) =>
                        handleLedgerChange(index, "ledgerId", e.target.value)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#FFFAF0",
                          fontSize: "0.875rem",
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                        Select Ledger
                      </MenuItem>
                      {ledgers.map((ledger) => (
                        <MenuItem
                          key={ledger._id}
                          value={ledger._id}
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {ledger.LegerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    mb={1}
                    display="block"
                  >
                    Debit
                  </Typography>
                  <TextField
                    type="number"
                    value={entry.debit}
                    onChange={(e) =>
                      handleLedgerChange(index, "debit", e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder="0.00"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFAF0",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    mb={1}
                    display="block"
                  >
                    Credit
                  </Typography>
                  <TextField
                    type="number"
                    value={entry.credit}
                    onChange={(e) =>
                      handleLedgerChange(index, "credit", e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder="0.00"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFAF0",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    mb={1}
                    display="block"
                  >
                    Description
                  </Typography>
                  <TextField
                    type="text"
                    value={entry.description}
                    onChange={(e) =>
                      handleLedgerChange(index, "description", e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder="Description"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFAF0",
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>
      ));
    } else {
      // Desktop table layout
      return (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: theme.palette.text.primary,
                    borderBottom: "2px solid #E9ECEF",
                  }}
                >
                  Ledger Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: theme.palette.text.primary,
                    borderBottom: "2px solid #E9ECEF",
                  }}
                >
                  Debit
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: theme.palette.text.primary,
                    borderBottom: "2px solid #E9ECEF",
                  }}
                >
                  Credit
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: theme.palette.text.primary,
                    borderBottom: "2px solid #E9ECEF",
                  }}
                >
                  Description
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {journalData.ledgerEntries.map((entry, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  component={TableRow}
                  sx={{
                    borderBottom: "1px solid #E9ECEF",
                    "&:hover": { backgroundColor: "#F8F9FA" },
                  }}
                >
                  <TableCell sx={{ padding: "16px", verticalAlign: "middle" }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={entry.ledgerId}
                        onChange={(e) =>
                          handleLedgerChange(index, "ledgerId", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFAF0",
                            fontSize: "0.875rem",
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                          Select Ledger
                        </MenuItem>
                        {ledgers.map((ledger) => (
                          <MenuItem
                            key={ledger._id}
                            value={ledger._id}
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {ledger.LegerName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ padding: "16px", verticalAlign: "middle" }}>
                    <TextField
                      type="number"
                      value={entry.debit}
                      onChange={(e) =>
                        handleLedgerChange(index, "debit", e.target.value)
                      }
                      size="small"
                      fullWidth
                      placeholder="0.00"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#FFFAF0",
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "16px", verticalAlign: "middle" }}>
                    <TextField
                      type="number"
                      value={entry.credit}
                      onChange={(e) =>
                        handleLedgerChange(index, "credit", e.target.value)
                      }
                      size="small"
                      fullWidth
                      placeholder="0.00"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#FFFAF0",
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "16px", verticalAlign: "middle" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        type="text"
                        value={entry.description}
                        onChange={(e) =>
                          handleLedgerChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        size="small"
                        fullWidth
                        placeholder="Description"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFAF0",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                      {journalData.ledgerEntries.length > 1 && (
                        <Tooltip title="Delete Entry">
                          <IconButton
                            onClick={() => handleDeleteLedger(index)}
                            size="small"
                            sx={{
                              color: theme.palette.error.main,
                              "&:hover": {
                                backgroundColor: "rgba(231, 76, 60, 0.08)",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  // Calculate totals
  const totalDebit = journalData.ledgerEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.debit) || 0),
    0
  );
  const totalCredit = journalData.ledgerEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.credit) || 0),
    0
  );

  return (
    <Layout>
      <Box
        sx={{
          p: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Grid container spacing={3}>
          {/* Main Form Section */}
          <Grid item xs={12} lg={isLargeScreen ? 9 : 8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card elevation={2} sx={{ borderRadius: "16px" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={4}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <PostAddIcon
                        sx={{ fontSize: 32, color: theme.palette.primary.main }}
                      />
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="primary"
                      >
                        Journal Posting
                      </Typography>
                    </Box>
                    <SearchBar />
                  </Box>

                  <form onSubmit={handleSubmit}>
                    {/* Entry Type Selection */}
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight="600" mb={2}>
                        Entry Type
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 250 }}>
                        <Select
                          value={journalData.selectedValue}
                          onChange={(e) => {
                            const entry = adjustmententry.find(
                              (entry) => entry.value === e.target.value
                            );
                            handleInputChange("selectedValue", e.target.value);
                            handleInputChange("selectedEntry", entry);
                          }}
                          displayEmpty
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#FFFAF0",
                            },
                          }}
                        >
                          <MenuItem value="">Select Entry Type</MenuItem>
                          {adjustmententry.map((entry, index) => (
                            <MenuItem key={index} value={entry.value}>
                              {entry.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {journalData.selectedEntry && (
                      <Box mb={3}>
                        <Journalentry
                          heading={journalData.selectedEntry.heading}
                          headingto={journalData.selectedEntry.headingto}
                        />
                      </Box>
                    )}

                    {/* Journal Header Section */}
                    <Box mb={3}>
                      <Typography variant="h5" fontWeight="600">
                        Journal Entry
                      </Typography>
                    </Box>

                    {/* Journal Details */}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: "12px",
                        backgroundColor: "#FFFAF0",
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            mb={1}
                          >
                            Journal No.
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={journalData.journalNo}
                              onChange={(e) =>
                                handleInputChange("journalNo", e.target.value)
                              }
                            >
                              <MenuItem value="Automatic">Automatic</MenuItem>
                              <MenuItem value="Manual">Manual</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {journalData.journalNo === "Manual" && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              mb={1}
                            >
                              Manual Entry
                            </Typography>
                            <TextField
                              type="text"
                              value={journalData.manualEntryData}
                              onChange={(e) =>
                                handleInputChange(
                                  "manualEntryData",
                                  e.target.value
                                )
                              }
                              size="small"
                              fullWidth
                              placeholder="Enter journal number"
                            />
                          </Grid>
                        )}

                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          sx={{
                            "& .react-datepicker-wrapper": {
                              width: "100%",
                              border: "none !important",
                              boxShadow: "none !important",
                              background: "transparent !important",
                              padding: 0,
                              margin: 0,
                            },
                            "& .react-datepicker__input-container": {
                              width: "100%",
                              border: "none !important",
                              boxShadow: "none !important",
                              background: "transparent !important",
                              padding: 0,
                              margin: 0,
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            mb={1}
                          >
                            Invoice Date
                          </Typography>

                          <DatePicker
                            selected={journalData.invoiceDate}
                            onChange={(date) =>
                              handleInputChange("invoiceDate", date)
                            }
                            customInput={
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Select date"
                                inputProps={{
                                  style: { padding: "10.5px 14px" },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    cursor: "pointer",
                                    height: "40px",
                                    "& fieldset": {
                                      border: "1px solid #ccc",
                                    },
                                  },
                                }}
                              />
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="600"
                            mb={1}
                          >
                            Invoice No.
                          </Typography>
                          <TextField
                            type="text"
                            value={journalData.invoiceNo}
                            onChange={(e) =>
                              handleInputChange("invoiceNo", e.target.value)
                            }
                            size="small"
                            fullWidth
                            placeholder="Invoice number"
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Ledger Entries Section */}
                    <Paper
                      elevation={1}
                      sx={{ borderRadius: "12px", overflow: "hidden", mb: 3 }}
                    >
                      <Box
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          p: 2,
                        }}
                      >
                        <Typography variant="h6" fontWeight="600">
                          Ledger Entries
                        </Typography>
                      </Box>

                      <Box sx={{ p: isMobile ? 2 : 0 }}>
                        {renderResponsiveLedgerEntries()}
                      </Box>

                      {/* Add Entry Button */}
                      <Box
                        display="flex"
                        justifyContent="center"
                        p={2}
                        sx={{ backgroundColor: "#FFFAF0" }}
                      >
                        <Button
                          onClick={handleAddLedger}
                          startIcon={<AddBoxIcon />}
                          variant="outlined"
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                        >
                          Add Ledger Entry
                        </Button>
                      </Box>

                      {/* Totals */}
                      <Box
                        sx={{
                          backgroundColor: "#F8F9FA",
                          p: 2,
                          borderTop: "2px solid #E9ECEF",
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="subtitle1" fontWeight="600">
                                Total Debit:
                              </Typography>
                              <Chip
                                label={totalDebit.toFixed(2)}
                                color="info"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="subtitle1" fontWeight="600">
                                Total Credit:
                              </Typography>
                              <Chip
                                label={totalCredit.toFixed(2)}
                                color="success"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                        {totalDebit !== totalCredit && (
                          <Box mt={2} display="flex" justifyContent="center">
                            <Chip
                              label="Entry not balanced!"
                              color="error"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Paper>

                    {/* Action Buttons */}
                    <Box
                      display="flex"
                      gap={2}
                      flexWrap="wrap"
                      justifyContent={isMobile ? "center" : "flex-start"}
                    >
                      <Button
                        onClick={() =>
                          handleDeleteLedger(
                            journalData.ledgerEntries.length - 1
                          )
                        }
                        startIcon={<DeleteIcon />}
                        variant="outlined"
                        color="error"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Delete Last
                      </Button>
                      <Button
                        onClick={handleAddLedger}
                        startIcon={<AddBoxIcon />}
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Add Entry
                      </Button>
                      <Button
                        onClick={() => {
                          setJournalData({
                            ...journalData,
                            ledgerEntries: [
                              {
                                ledgerName: "",
                                debit: "",
                                credit: "",
                                description: "",
                              },
                            ],
                          });
                          showToast("Form reset successfully", "info");
                        }}
                        startIcon={<RefreshIcon />}
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        startIcon={<SaveIcon />}
                        variant="contained"
                        size="large"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 4,
                        }}
                      >
                        Submit Journal
                      </Button>
                    </Box>
                  </form>

                  <Divider sx={{ my: 4 }} />
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Quick Actions Section */}
          <Grid item xs={12} lg={isLargeScreen ? 3 : 4}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card elevation={2} sx={{ borderRadius: "16px" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" mb={3}>
                    Quick Actions
                  </Typography>

                  {/* Grid layout for small/medium screens */}
                  <Grid
                    container
                    spacing={2}
                    sx={{ display: { xs: "flex", lg: "none" } }}
                  >
                    {[
                      "Purchase",
                      "Sales",
                      "Income",
                      "Expenses",
                      "Credit",
                      "Debit Note",
                      "Adjustments",
                    ].map((buttonText, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{
                              py: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "12px",
                              "&:hover": {
                                backgroundColor: theme.palette.primary.main,
                                color: "white",
                                transform: "translateY(-2px)",
                                transition: "all 0.2s ease-in-out",
                              },
                            }}
                          >
                            {buttonText}
                          </Button>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Column layout for large screens */}
                  <Box
                    sx={{
                      display: { xs: "none", lg: "flex" },
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {[
                      "Purchase",
                      "Sales",
                      "Income",
                      "Expenses",
                      "Credit",
                      "Debit Note",
                      "Adjustments",
                    ].map((buttonText, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          fullWidth
                          variant="outlined"
                          sx={{
                            py: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                            justifyContent: "flex-start",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: "white",
                              transform: "translateY(-2px)",
                              transition: "all 0.2s ease-in-out",
                            },
                          }}
                        >
                          {buttonText}
                        </Button>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
      <Box>
        <AdjustmentEntry
          journalEntries={journalEntries}
          kycId={selectedCompanyId}
          heading="Cash"
          headingto="Sales"
        />
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={hideToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideToast}
          severity={toast.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Journal;
